import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Button, Card, CardContent, Form, LoadingButton, Stack, toast } from "@altimateai/lego";
import { getIconByDatastoreType } from "../../DatastoreImage";
import { ConnectionProps } from "./types";
import { AzureDatabricksConnectionRequest, DatastoreTypeEnum } from "@/pages/datastores/types";
import { saveAzureDatabricksConnection } from "@/helpers/apis";
import { showAzureDatabricksConnections } from "@/helpers/tenant";
import {
  azureDatabricksFormSchema,
  type AzureDatabricksFormValues,
} from "./AzureDatabricksConnection/schema";
import { AzureDatabricksFields } from "./AzureDatabricksConnection/Fields";

const AzureDatabricksConnection = ({ typeData, handleBack }: ConnectionProps): JSX.Element => {
  const navigate = useNavigate();
  const form = useForm<AzureDatabricksFormValues>({
    resolver: zodResolver(azureDatabricksFormSchema),
    defaultValues: {
      azure_tenant_id: "",
      databricks_account_id: "",
      workspace_url: "",
      sp_client_id: "",
      sp_secret: "",
      sql_warehouse_id: "",
      system_catalog_scope: "all",
      system_catalog_schemas: "",
    },
    mode: "onBlur",
  });

  const { mutate: saveConnection, isPending: isSaving } = useMutation({
    mutationFn: (data: AzureDatabricksConnectionRequest) => saveAzureDatabricksConnection(data),
    onSuccess: () => {
      toast({
        title: "Saved",
        description: "Azure Databricks connection saved successfully",
      });
      navigate("/settings/datastores");
    },
    onError: (err: unknown) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: (err as Error)?.message || "Unable to save Azure Databricks connection",
      });
    },
  });

  const onSubmit = (values: AzureDatabricksFormValues) => {
    const payload: AzureDatabricksConnectionRequest = {
      name: typeData.name,
      description: typeData.description,
      ...values,
    };
    saveConnection(payload);
  };

  const showTestConnection = !showAzureDatabricksConnections();

  return (
    <Card>
      <CardContent>
        <Stack direction="row" justify="end" className="al-items-center al-mt-2">
          <Stack direction="row" className="al-items-center al-gap-2">
            {getIconByDatastoreType({
              datastoreType: DatastoreTypeEnum.AZURE_DATABRICKS,
              className: "al-w-8 al-h-8",
            })}
            <span className="al-text-base al-font-medium">Azure Databricks</span>
          </Stack>
        </Stack>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="al-mt-6">
            <Stack gap="xl" direction="column">
              <AzureDatabricksFields control={form.control} watch={form.watch} />

              <Stack direction="row" justify="between">
                <Button variant="outline" asChild>
                  <Link to="/settings/datastores">Cancel</Link>
                </Button>
                <Stack gap="md">
                  <Button variant="secondary" onClick={handleBack}>
                    Back
                  </Button>
                  {showTestConnection && (
                    <Button variant="default" disabled>
                      Test Connection
                    </Button>
                  )}
                  <LoadingButton
                    variant="default"
                    type="submit"
                    loading={isSaving}
                    className="al-px-4"
                    disabled={!form.formState.isValid}
                  >
                    Save Connection
                  </LoadingButton>
                </Stack>
              </Stack>
            </Stack>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default AzureDatabricksConnection;
